import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, RotateCcw } from "lucide-react";

const ST = {
  operational: { color: "#22C55E", icon: CheckCircle2, label: "OPERATIONAL" },
  degraded: { color: "#F59E0B", icon: AlertTriangle, label: "DEGRADED" },
  down: { color: "#EF4444", icon: XCircle, label: "DOWN" },
};

export default function SystemStatus() {
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const load = useCallback(async () => {
    try {
      const [s, q] = await Promise.all([client.get("/system/status"), client.get("/system/retry-queue")]);
      setStatus(s.data); setQueue(q.data.items || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const resolve = async (id) => {
    try { await client.post(`/system/retry-queue/${id}/resolve`); toast.success("Queue item replayed/resolved"); load(); }
    catch { toast.error("Resolve failed"); }
  };
  const overall = ST[status?.overall] || ST.operational;

  return (
    <Layout>
      <PageHeader title="Continuity & System Status" sub="Service availability · degraded-mode alerts · retry queue · auto-refresh 15s"
        right={<button data-testid="status-refresh" onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white rounded-sm"><RefreshCw size={12} /> REFRESH</button>} />
      <div className="flex-1 overflow-y-auto p-5" data-testid="system-status-page">
        {status && (
          <div className="flex items-center gap-3 border px-4 py-3 mb-4 rounded-sm" style={{ borderColor: `${overall.color}55`, background: `${overall.color}0D` }} data-testid="system-overall">
            <overall.icon size={20} style={{ color: overall.color }} />
            <div>
              <div className="font-head font-bold text-sm" style={{ color: overall.color }}>SYSTEM {overall.label}</div>
              <div className="font-mono text-[10px] text-[#9CA3AF]">Last check {status.checked_at ? new Date(status.checked_at).toLocaleTimeString("en-GB", { hour12: false }) : "—"} · {status.overall !== "operational" ? "Degraded mode active — terminals fall back to cached data and queue outbound work for replay." : "All services nominal."}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Retry queue</div>
              <div className="font-mono font-bold text-xl" style={{ color: status.retry_queue?.pending ? "#F59E0B" : "#22C55E" }}>{status.retry_queue?.pending ?? 0}</div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(status?.services || []).map((s) => {
            const st = ST[s.status] || ST.down;
            return (
              <div key={s.key} data-testid={`service-${s.key}`} className="border border-[#2C2D35] bg-[#141518] p-3 rounded-sm">
                <div className="flex items-center gap-2">
                  <st.icon size={15} style={{ color: st.color }} />
                  <span className="font-head font-semibold text-sm text-white">{s.service}</span>
                  <span className="ml-auto font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm border" style={{ color: st.color, borderColor: `${st.color}55` }}>{st.label}</span>
                </div>
                <div className="font-mono text-[10px] text-[#9CA3AF] mt-1.5">{s.detail}{s.latency_ms != null ? ` · ${s.latency_ms}ms` : ""}</div>
              </div>
            );
          })}
        </div>

        <div className="border border-[#2C2D35] mt-5 rounded-sm" data-testid="retry-queue">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
            <RotateCcw size={13} className="text-[#F59E0B]" /> RETRY QUEUE — WORK CAPTURED DURING DEGRADED MODE
            <span className="ml-auto font-mono text-[10px] text-[#9CA3AF]">{queue.length} item(s)</span>
          </div>
          {queue.length === 0 ? (
            <div className="p-3 font-mono text-[11px] text-[#6B7280]">Queue empty. When a service is degraded, failed outbound work (notifications, federal queries, uploads) is captured here for replay.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase">
                <tr><th className="px-3 py-2">Time</th><th className="px-2">Kind</th><th className="px-2">Note</th><th className="px-2">By</th><th className="px-2">Status</th><th className="px-2" /></tr>
              </thead>
              <tbody>
                {queue.map((it) => (
                  <tr key={it.id} className="border-b border-[#1E1F24]" data-testid={`queue-item-${it.id}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-[#6B7280]">{it.ts ? new Date(it.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</td>
                    <td className="px-2 font-mono text-[10px] uppercase text-[#22D3EE]">{it.kind}</td>
                    <td className="px-2 text-xs">{it.note || "—"}</td>
                    <td className="px-2 text-xs">{it.by}</td>
                    <td className="px-2 font-mono text-[10px] uppercase" style={{ color: it.status === "pending" ? "#F59E0B" : "#22C55E" }}>{it.status}</td>
                    <td className="px-2">
                      {it.status === "pending" && (
                        <button data-testid={`queue-resolve-${it.id}`} onClick={() => resolve(it.id)}
                          className="px-2 py-1 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase">Replay / Resolve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
